import Group from 'components/utils/Group'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import useCopyWithFeedback from 'hooks/useCopyWithFeedback'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import formatStaticAppText from 'lib/format/formatStaticAppText'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import WineSelectorsBackground from './WineSelectorsModalImage'

const PromoClickableListItem = styled(ClickableListItem)`
  background-color: ${props => props.theme.palette.neutral.default.six};
  max-width: ${rem(336)};
`

interface Props {
  benefit?: App.MemberSubscriptionBenefitItem
}

function WineSelectorsCampaignModal({ benefit }: Props) {
  const intl = useIntl()
  const code = benefit?.code ?? (benefit ? 'LUXURYESCAPES50' : undefined)
  const programName = formatStaticAppText(intl, LUXURY_PLUS.PROGRAM_NAME)
  const partnerName = intl.formatMessage({
    id: 'hrZ7-0x5mUuL0cxw2LDA',
    defaultMessage: 'Wine Selectors',
    description: 'Partner brand name shown in the LuxPlus+ Wine Selectors campaign modal',
  })
  const countryName = intl.formatDisplayName('AU', { type: 'region' }) ?? intl.formatMessage({
    id: 'yeQaE_pzT5EL0cxczNnA',
    defaultMessage: 'Australia',
    description: 'Country name shown in the LuxPlus+ Wine Selectors campaign modal copy and disclaimer',
  })

  const { isCopied, handleCopy } = useCopyWithFeedback(code ?? '')

  const titleText = <FormattedMessage
    id="FavhqmIahvKiew9LSvVR"
    defaultMessage="Exclusive {partnerName} Offer"
    description="Heading title for Wine Selectors partner campaign modal"
    values={{
      partnerName,
    }}
  />

  const bodyText1 = intl.formatMessage(
    {
      id: 'UWPApxcRTDHAtutZKuq2',
      defaultMessage: 'Elevate your at-home indulgence with {partnerName}. As a {programName} member, enjoy 50% off selected premium wine cases, curated by {countryName}’s leading wine experts.',
      description: 'Body text 1 for Wine Selectors partner campaign modal',
    },
    { partnerName, programName, countryName },
  )

  const bodyText2 = intl.formatMessage(
    {
      id: '12sw3ddqxODhf9Xz4TjL',
      defaultMessage: 'Discover exceptional reds, crisp whites and celebratory sparklings delivered direct to your door. Simply copy your unique code below and apply it at checkout to unlock your exclusive savings.',
      description: 'Body text 2 for Wine Selectors partner campaign modal',
    },
  )

  const shopButtonText = intl.formatMessage({
    id: 'RJZWcSjCUwYmJJP0cKEX',
    defaultMessage: 'Shop {partnerName}',
    description: 'Button text to shop Wine Selectors from partner campaign modal',
  },
  { partnerName },
  )

  return (
    <Modal
      size="L"
      title={titleText}
      primaryActionText={benefit ? shopButtonText : undefined}
      primaryActionHref="https://www.wineselectors.com.au/wine-shop/luxury-escapes-50-offer?rows=12&start=0&utm_source=LuxuryEscapes&utm_medium=email&utm_campaign=lux-plus"
      primaryActionTarget="_blank"
    >
      <Group direction="vertical" gap={20}>
        <WineSelectorsBackground />
        <div>
          <BodyText variant="medium">
            {bodyText1}
            <br />
            <br />
            {bodyText2}
            <br />
            <br />
          </BodyText>
        </div>
        {code && <PromoClickableListItem
          title={<b>{code}</b>}
          endIcon={isCopied ? <LineCheckIcon size="S" /> : <LineCopyIcon size="S" />}
          onClick={handleCopy}
          selected={isCopied}
        />}

        <BodyText variant="small">
          <FormattedMessage
            id="5rrGuPGK6M_56TeIgubb"
            defaultMessage="Promo code is valid in {countryName} only. <link>Terms and conditions apply.</link>"
            description="Disclaimer with terms link for Wine Selectors partner campaign"
            values={{
              countryName,
              link: (chunks) => <TextLink
                target="_blank"
                to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}
                weight="regular"
              >{chunks}
              </TextLink>,
            }}
          />
        </BodyText>
      </Group>
    </Modal>
  )
}

export default WineSelectorsCampaignModal
